import React, { useEffect, useState, createContext, useRef, useMemo } from 'react';
import axios from '@common/axios';
import useCurrentShortcut from "@hooks/useCurrentShortcut";
import CircularLoader from '@common/CircularLoader';
import { Alert, message } from 'antd';
import FrozenStepManager from './FrozenStepManager';
import { Timeline, NavigationButtons, ContentArea } from './TrailSubcomponents';

const TrailsContext = createContext({ updateContinue: () => {} });

function StudentTrail() {
    const { loadShortcutByUrl } = useCurrentShortcut();

    const [steps, setSteps] = useState([]);
    const [stepIndex, setStepIndex] = useState(-1);
    // REMOVED: currentStep state (it is now derived below)

    const [currentStepActivity, setCurrentStepActivity] = useState();
    const [stepUserResult, setStepUserResult] = useState();
    const [stepActivities, setStepActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stepLoading, setStepLoading] = useState(false);
    const [error, setError] = useState();
    const [pathwayAssignmentId, setPathwayAssignmentId] = useState();
    const [pathwayUser, setPathwayUser] = useState();
    const [refreshStepResult, setRefreshStepResult] = useState(0);

    const [allowContinueStep, setAllowContinueStep] = useState(false);

    // FIX: Derived state (prevents UI flicker/sync issues)
    const currentStep = (stepIndex >= 0 && stepIndex < steps.length) ? steps[stepIndex] : null;

    // FIX: Keep ref updated for event listeners (Stale Closure Fix)
    const currentStepRef = useRef(currentStep);
    currentStepRef.current = currentStep; // Update on every render

    // FIX: Track which stepId the updateStepResult event was for (prevents wrong-step refresh)
    const pendingRefreshStepIdRef = useRef(null);

    const handleBack = () => {
        if (pathwayUser?.multiConcept) {
            loadShortcutByUrl('/web/ui/view/studentTrailLessons', undefined, pathwayAssignmentId);
        } else {
            loadShortcutByUrl('/web/ui/view/trail/studentTrails');
        }
    };

    const continueCurrentStep = async () => {
        if (!currentStep || stepLoading) return;
        setStepLoading(true);
        try {
            await axios.post('/web/ui/trail/step/continueCurrentStep', null, {
                params: {
                    stepId: currentStep.stepId,
                    pathwayUserId: pathwayUser.pathwayUserId,
                },
            });
            await loadStepsAndResult(pathwayUser.pathwayUserId);
        } catch (err) {
            console.error(err);
            message.error('Failed to continue step.');
        } finally {
            setStepLoading(false);
        }
    };

    const handleSkipStep = async () => {
        if (!currentStep || stepLoading) return;
        setStepLoading(true);
        try {
            await axios.post('/web/ui/trail/step/skipStep', null, {
                params: {
                    stepId: currentStep.stepId,
                    stepUserId: currentStep.stepUserId,
                    pathwayUserId: pathwayUser.pathwayUserId,
                },
            });
            await loadStepsAndResult(pathwayUser.pathwayUserId);
        } catch (err) {
            console.error(err);
            message.error('Failed to skip step.');
        } finally {
            setStepLoading(false);
        }
    };

    const onStepChange = (targetIndex) => {
        if (targetIndex >= steps.length || targetIndex < 0) return;
        setStepIndex(targetIndex);
    };

    const loadStepsAndResult = async (locPathwayUserId, initialize = false) => {
        if (!locPathwayUserId) return;

        try {
            const stepsRes = await axios.get('/web/ui/trail/step/stepProgress', {
                params: { pathwayUserId: locPathwayUserId },
            });

            let newSteps = stepsRes.data.steps;
            const allCompleteOrSkipped = newSteps.every(
                step => step.complete || step.skipStep
            );

            const endStep = {
                stepId: -1,
                stepName: 'End',
                complete: allCompleteOrSkipped,
                allowContinue: false,
                allowSkip: false,
                currentStep: allCompleteOrSkipped,
                endStep: true,
                tabComponent: 'EndActivity',
                resultComponent: 'EndActivity',
                disabled: !allCompleteOrSkipped,
                handleBack: handleBack,
            };

            newSteps = [...newSteps, endStep];

            const newPathwayUser = {
                ...stepsRes.data.pathwayUser,
                allCompleteOrSkipped: allCompleteOrSkipped,
            };

            setPathwayUser(newPathwayUser);
            setSteps(newSteps);

            // FIX: Better initialization logic for completed trails
            let newStepIndex = 0;
            if (allCompleteOrSkipped || stepsRes.data.pathwayUser.complete) {
                // If complete, always go to end, even on initialize
                newStepIndex = newSteps.length - 1;
            } else {
                newStepIndex = newSteps.findIndex(step => step.currentStep);
                if (newStepIndex === -1) newStepIndex = 0;
            }

            setStepIndex(newStepIndex);
        } catch (err) {
            console.error(err);
            setError(err.message);
        }
    };

    const initializeTrail = async (assignmentId) => {
        try {
            setLoading(true);
            const trailRes = await axios.get('/web/ui/trail/loadTrail', { params: { pathwayAssignmentId: assignmentId } });
            const pathwayUserId = trailRes.data.pathwayUserId;
            await loadStepsAndResult(pathwayUserId, true);
        } catch (err) {
            console.error(err);
            message.error('Failed to load trail.');
        } finally {
            setLoading(false);
        }
    };

    const loadStep = async (step) => {
        try {
            setLoading(true);
            const res = await axios.get('/web/ui/trail/step/loadStep', {
                params: { pathwayUserId: step.pathwayUserId, stepId: step.stepId },
            });

            // FIX: Race Condition Check
            // Verify if the loaded step is still the currently selected step
            if (currentStepRef.current && currentStepRef.current.stepId !== step.stepId) {
                return;
            }

            setStepActivities(res.data.stepActivities);
            setCurrentStepActivity(res.data.stepActivity);
            setStepUserResult(res.data.stepUserResult);
            setError(undefined);
        } catch (err) {
            console.error(err);
            setError('Failed to load step.');
        } finally {
            setLoading(false);
        }
    };

    const loadUserResult = (pathwayUserId, stepId) => {
        if (pathwayUserId == undefined || stepId == undefined) return;

        axios
            .get('/web/ui/trail/step/userResult', { params: { pathwayUserId, stepId } })
            .then((response) => {
                setStepUserResult(response.data);
            }).catch((err) => {
            console.error(err);
            setError(err.message);
        });
    };

    useEffect(() => {
        // FIX: only refresh if the event was for the currently selected step
        if (refreshStepResult === 0) return;

        const cs = currentStepRef.current;
        const pendingStepId = pendingRefreshStepIdRef.current;

        if (!cs || pendingStepId == null || cs.stepId !== pendingStepId) return;

        loadUserResult(cs.pathwayUserId, cs.stepId);
    }, [refreshStepResult]);
    // Keep minimal deps; use refs to avoid closure/stale-step issues

    useEffect(() => {
        const hashValue = parseInt(window.location.hash.slice(1), 10);
        setPathwayAssignmentId(Number.isInteger(hashValue) ? hashValue : null);

        const handleUpdateStepResult = (event) => {
            const { stepId } = event.detail || {};
            // Use Ref to access latest state without closure issues
            const cs = currentStepRef.current;
            if (!cs || cs.stepId !== stepId) return;

            // FIX: record which step triggered the refresh
            pendingRefreshStepIdRef.current = stepId;

            setRefreshStepResult(prev => prev + 1);
        };

        const handleHashChange = () => {
            const hashNumericValue = parseInt(window.location.hash.slice(1), 10);
            setPathwayAssignmentId(Number.isInteger(hashNumericValue) ? hashNumericValue : null);
        };

        const handleCardChanged = () => {
            console.log('handleCardChanged');
        };

        const continueTrail = () => {
            if (!currentStep?.endStep) setAllowContinueStep(true);
        }

        window.addEventListener('hashchange', handleHashChange);
        window.addEventListener('updateStepResult', handleUpdateStepResult);
        window.addEventListener('cardChanged', handleCardChanged);
        window.addEventListener('ch-ev-allowContinueTrail', continueTrail);
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
            window.removeEventListener('updateStepResult', handleUpdateStepResult);
            window.removeEventListener('cardChanged', handleCardChanged);
            window.removeEventListener('ch-ev-allowContinueTrail', continueTrail);
        };
    }, []);

    useEffect(() => {
        if (!pathwayAssignmentId) return;
        initializeTrail(pathwayAssignmentId);
    }, [pathwayAssignmentId]);

    // Replaced the useEffect that syncs stepIndex -> currentStep
    // Instead, we just trigger loadStep when the derived currentStep changes
    useEffect(() => {
        if (!currentStep) return;

        // FIX: clear step-scoped state to prevent flashing stale data from previous step
        setCurrentStepActivity(undefined);
        setStepActivities([]);
        setStepUserResult(undefined);

        if (!currentStep.endStep) {
            loadStep(currentStep);
        }
    }, [currentStep]); // Reacts to changes in the derived object

    useEffect(() => {
        setAllowContinueStep(!(currentStep?.endStep || !stepUserResult?.allowContinue));
    }, [currentStep, stepUserResult]);

    // FIX: Memoize Context to prevent thrashing
    const context_values = useMemo(() => ({
        updateContinue: allowContinue => {
            if (!currentStep?.endStep) setAllowContinueStep(allowContinue);
        }
    }), [currentStep?.endStep]); // Only recreate if endStep status changes

    if (loading && !currentStep) return <CircularLoader />;

    if (!currentStep && !loading) {
        return <Alert showIcon={true} type="error" title="Activity is invalid" />;
    }

    if (error) {
        return <Alert role="alert" showIcon={true} message="Error" description={error} type="error" />;
    }

    const nonEndSteps = steps.filter(s => !s.endStep);
    const stepTotal = nonEndSteps.length;
    const stepNumber = (!currentStep || currentStep.endStep)
        ? null
        : (nonEndSteps.findIndex(s => s.stepId === currentStep.stepId) + 1);

    const skipStepDisabled = currentStep?.endStep || currentStep?.branchStep || currentStep?.autoComplete || stepLoading;

    return (
        <div key={pathwayAssignmentId} className="flexColumn mediumGap" style={{ padding: 'var(--spacing-unit)' }}>
            <Timeline
                steps={steps}
                stepIndex={stepIndex}
                pathwayUser={pathwayUser}
                onStepChange={onStepChange}
            />
            <NavigationButtons
                pathwayUser={pathwayUser}
                stepIndex={stepIndex}
                steps={steps}
                step={currentStep}
                onStepChange={onStepChange}
                handleBack={handleBack}
                handleSkipStep={handleSkipStep}
                disableSkipStep={skipStepDisabled}
                handleContinueCurrentStep={continueCurrentStep}
                disableContinueCurrentStep={!allowContinueStep || stepLoading}
                stepNumber={stepNumber}
                stepTotal={stepTotal}
            />

            {stepLoading ? <CircularLoader displayInElement={true} /> :
                <div style={{ position: 'relative' }}>
                    <TrailsContext.Provider value={context_values}>
                        <FrozenStepManager
                            currentStepId={currentStep?.stepId}
                            contentRenderer={(stepId) => {
                                const step = steps.find(s => s.stepId === stepId);
                                if (!step) return null;
                                const isCurrent = stepId === currentStep?.stepId;
                                const renderedStepNumber = nonEndSteps.findIndex(s => s.stepId === stepId) + 1;

                                return (
                                    <ContentArea
                                        step={isCurrent ? currentStep : { ...step }}
                                        stepActivity={isCurrent ? currentStepActivity : undefined}
                                        stepActivities={isCurrent ? stepActivities : []}
                                        stepUserResult={isCurrent ? stepUserResult : undefined}
                                        pathwayUser={pathwayUser}
                                        continueCurrentStep={continueCurrentStep}
                                        setStepUserResult={setStepUserResult}
                                        handleBack={handleBack}
                                        stepNumber={renderedStepNumber}
                                        stepTotal={stepTotal}
                                    />
                                );
                            }}
                            shouldFreezeStep={(stepId) => {
                                const step = steps.find(s => s.stepId === stepId);
                                return step?.skipStep === true;
                            }}
                            shouldUnfreezeStep={(stepId) => {
                                const step = steps.find(s => s.stepId === stepId);
                                return step?.complete === true;
                            }}
                        />
                    </TrailsContext.Provider>
                </div>
            }
        </div>
    );
}

export { StudentTrail, TrailsContext };
